// src/pages/inspector/InspectorInspectionDetail.jsx
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InspectorLayout from '../../components/inspector/InspectorLayout';
import inspectorAPI from '../../services/inspectorAPI';
import { StatusBadge } from './InspectorDashboard';
import ReportTab from '../../components/inspector/inspections/ReportTab';
import styles from './InspectorInspectionDetail.module.css';

const TIME_LABELS = { morning: '9AM–12PM', afternoon: '12PM–4PM', evening: '4PM–7PM' };

const INSPECTION_CATEGORIES = [
  'Engine', 'Transmission', 'Brakes', 'Suspension', 'Electrical',
  'Interior', 'Exterior', 'Tyres', 'AC & Heating', 'Lights', 'Documents',
];

const ITEM_STATUSES = [
  { value: 'good',        label: '✅ Good',        color: '#10b981' },
  { value: 'fair',        label: '⚠️ Fair',        color: '#f59e0b' },
  { value: 'poor',        label: '❌ Poor',        color: '#ef4444' },
  { value: 'not_checked', label: '⬜ Not Checked', color: '#94a3b8' },
];

const INSPECTION_STATUSES = ['pending', 'scheduled', 'in_progress', 'completed', 'approved', 'rejected'];

export default function InspectorInspectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [inspection, setInspection] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [error, setError]           = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [activeTab, setActiveTab]   = useState('overview');

  // Update form
  const [updateForm, setUpdateForm] = useState({
    status: '',
    notes: '',
    overall_rating: '',
    estimated_price: '',
    actual_inspection_date: '',
  });

  // New item form
  const [newItem, setNewItem] = useState({
    category: 'Engine',
    name: '',
    status: 'good',
    remarks: '',
  });
  const [addingItem, setAddingItem] = useState(false);

  // Photos
  const fileInputRef    = useRef(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);

  const loadInspection = useCallback(async () => {
    setLoading(true);
    try {
      const res = await inspectorAPI.getInspection(id);
      setInspection(res.data);
      setUpdateForm({
        status:                 res.data.status                 || '',
        notes:                  res.data.notes                  || '',
        overall_rating:         res.data.overall_rating         || '',
        estimated_price:        res.data.estimated_price        || '',
        actual_inspection_date: res.data.actual_inspection_date || '',
      });
    } catch {
      setError('Failed to load inspection details.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadInspection(); }, [loadInspection]);

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await inspectorAPI.updateInspection(id, updateForm);
      showSuccess('Inspection updated successfully!');
      loadInspection();
    } catch {
      setError('Failed to update inspection.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    setAddingItem(true);
    try {
      await inspectorAPI.createInspectionItem(id, newItem);
      setNewItem({ category: 'Engine', name: '', status: 'good', remarks: '' });
      showSuccess('Inspection item added!');
      loadInspection();
    } catch {
      setError('Failed to add inspection item.');
    } finally {
      setAddingItem(false);
    }
  };

  const handleUpdateItem = async (itemId, data) => {
    try {
      await inspectorAPI.updateInspectionItem(id, itemId, data);
      showSuccess('Item updated!');
      loadInspection();
    } catch {
      setError('Failed to update item.');
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingPhotos(true);
    try {
      await inspectorAPI.uploadPhotos(id, files);
      showSuccess(`${files.length} photo(s) uploaded!`);
      loadInspection();
    } catch {
      setError('Failed to upload photos.');
    } finally {
      setUploadingPhotos(false);
    }
  };

  // Called by ReportTab after a successful save so the dot indicator updates
  const handleReportSaved = (report) => {
    setInspection((prev) => ({ ...prev, inspection_report: report }));
  };

  if (loading) {
    return (
      <InspectorLayout>
        <div className={styles.loading}>Loading inspection…</div>
      </InspectorLayout>
    );
  }

  if (error && !inspection) {
    return (
      <InspectorLayout>
        <div className={styles.errorFull}>
          <span>⚠️</span>
          <p>{error}</p>
          <button onClick={() => navigate('/inspector/inspections')}>← Back</button>
        </div>
      </InspectorLayout>
    );
  }

  const { car, inspection_items = [], inspection_report } = inspection || {};

  // Group checklist items by category
  const itemsByCategory = (inspection_items || []).reduce((acc, item) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <InspectorLayout>
      <div className={styles.page}>

        {/* ── Toast messages ── */}
        {successMsg && <div className={styles.toastSuccess}>{successMsg}</div>}
        {error && (
          <div className={styles.toastError}>
            {error}
            <button onClick={() => setError('')}>×</button>
          </div>
        )}

        {/* ── Header ── */}
        <div className={styles.header}>
          <button className={styles.backBtn} onClick={() => navigate('/inspector/inspections')}>
            ← Back
          </button>
          <div className={styles.headerMain}>
            <div>
              <h1 className={styles.title}>
                {car?.brand} {car?.model}{' '}
                <span className={styles.year}>({car?.year})</span>
              </h1>
              <div className={styles.ref}>{inspection?.booking_reference}</div>
            </div>
            <StatusBadge status={inspection?.status} />
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className={styles.tabs}>
          {['overview', 'checklist', 'photos', 'report'].map((tab) => (
            <button
              key={tab}
              className={`${styles.tab} ${activeTab === tab ? styles.activeTab : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview'  && '📋 '}
              {tab === 'checklist' && '✅ '}
              {tab === 'photos'    && '📷 '}
              {tab === 'report'    && '📄 '}
              {tab.charAt(0).toUpperCase() + tab.slice(1)}

              {tab === 'checklist' && inspection_items?.length > 0 && (
                <span className={styles.tabBadge}>{inspection_items.length}</span>
              )}

              {/* Green dot when a report already exists */}
              {tab === 'report' && inspection_report && (
                <span className={styles.tabDot} title="Report exists" />
              )}
            </button>
          ))}
        </div>

        {/* ── Tab Content ── */}
        <div className={styles.tabContent}>

          {/* ════════ OVERVIEW ════════ */}
          {activeTab === 'overview' && (
            <div className={styles.overviewGrid}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Car Details</h3>
                <InfoRow label="Brand"     value={car?.brand} />
                <InfoRow label="Model"     value={car?.model} />
                <InfoRow label="Year"      value={car?.year} />
                <InfoRow label="KM Driven" value={`${car?.km_driven?.toLocaleString()} km`} />
              </div>

              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Inspection Details</h3>
                <InfoRow label="Reference"      value={inspection?.booking_reference} mono />
                <InfoRow label="City"           value={inspection?.city} />
                <InfoRow
                  label="Preferred Date"
                  value={new Date(inspection?.preferred_date).toLocaleDateString('en-IN', {
                    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
                  })}
                />
                <InfoRow label="Time Slot"      value={TIME_LABELS[inspection?.preferred_time] || inspection?.preferred_time} />
                <InfoRow label="Contact Person" value={inspection?.contact_person} />
                <InfoRow label="Contact Phone"  value={inspection?.contact_phone} mono />
                {inspection?.location && <InfoRow label="Location" value={inspection.location} />}
              </div>

              {/* Update Form */}
              <div className={`${styles.card} ${styles.fullWidth}`}>
                <h3 className={styles.cardTitle}>Update Inspection</h3>
                <form onSubmit={handleUpdate} className={styles.updateForm}>
                  <div className={styles.formRow}>
                    <div className={styles.formField}>
                      <label>Status</label>
                      <select
                        value={updateForm.status}
                        onChange={(e) => setUpdateForm({ ...updateForm, status: e.target.value })}
                      >
                        {INSPECTION_STATUSES.map((s) => (
                          <option key={s} value={s}>{s.replace('_', ' ')}</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.formField}>
                      <label>Overall Rating (1–10)</label>
                      <input
                        type="number" min="1" max="10"
                        value={updateForm.overall_rating}
                        onChange={(e) => setUpdateForm({ ...updateForm, overall_rating: e.target.value })}
                        placeholder="e.g. 7"
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>Estimated Price (₹)</label>
                      <input
                        type="number"
                        value={updateForm.estimated_price}
                        onChange={(e) => setUpdateForm({ ...updateForm, estimated_price: e.target.value })}
                        placeholder="e.g. 450000"
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>Actual Inspection Date</label>
                      <input
                        type="date"
                        value={updateForm.actual_inspection_date}
                        onChange={(e) => setUpdateForm({ ...updateForm, actual_inspection_date: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className={styles.formField}>
                    <label>Inspector Notes</label>
                    <textarea
                      rows={4}
                      value={updateForm.notes}
                      onChange={(e) => setUpdateForm({ ...updateForm, notes: e.target.value })}
                      placeholder="Add your inspection notes here…"
                    />
                  </div>
                  <button type="submit" className={styles.saveBtn} disabled={saving}>
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                </form>
              </div>
            </div>
          )}

          {/* ════════ CHECKLIST ════════ */}
          {activeTab === 'checklist' && (
            <div className={styles.checklistContainer}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Add Inspection Item</h3>
                <form onSubmit={handleAddItem} className={styles.addItemForm}>
                  <div className={styles.formRow}>
                    <div className={styles.formField}>
                      <label>Category</label>
                      <select
                        value={newItem.category}
                        onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                      >
                        {INSPECTION_CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div className={styles.formField}>
                      <label>Item Name</label>
                      <input
                        type="text" required
                        value={newItem.name}
                        onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                        placeholder="e.g. Engine Oil, Left Brake Pad"
                      />
                    </div>
                    <div className={styles.formField}>
                      <label>Status</label>
                      <select
                        value={newItem.status}
                        onChange={(e) => setNewItem({ ...newItem, status: e.target.value })}
                      >
                        {ITEM_STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className={styles.formField}>
                    <label>Remarks</label>
                    <input
                      type="text"
                      value={newItem.remarks}
                      onChange={(e) => setNewItem({ ...newItem, remarks: e.target.value })}
                      placeholder="Optional remarks…"
                    />
                  </div>
                  <button type="submit" className={styles.saveBtn} disabled={addingItem}>
                    {addingItem ? 'Adding…' : '+ Add Item'}
                  </button>
                </form>
              </div>

              {Object.keys(itemsByCategory).length === 0 ? (
                <div className={styles.emptyChecklist}>
                  <span>📋</span>
                  <p>No inspection items yet. Add items using the form above.</p>
                </div>
              ) : (
                Object.entries(itemsByCategory).map(([category, items]) => (
                  <div key={category} className={styles.card}>
                    <h3 className={styles.cardTitle}>{category}</h3>
                    <div className={styles.itemsTable}>
                      <div className={styles.tableHeader}>
                        <span>Item</span>
                        <span>Status</span>
                        <span>Remarks</span>
                        <span>Action</span>
                      </div>
                      {items.map((item) => (
                        <InspectionItemRow key={item.id} item={item} onUpdate={handleUpdateItem} />
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* ════════ PHOTOS ════════ */}
          {activeTab === 'photos' && (
            <div className={styles.photosContainer}>
              <div className={styles.card}>
                <h3 className={styles.cardTitle}>Upload Photos</h3>
                <div className={styles.dropzone} onClick={() => fileInputRef.current?.click()}>
                  <span>📷</span>
                  <p>Click to select photos</p>
                  <small>JPG, PNG, HEIC — multiple files supported</small>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload}
                  />
                </div>
                {uploadingPhotos && (
                  <div className={styles.uploadProgress}>Uploading photos…</div>
                )}
              </div>

              {inspection?.inspection_images?.length > 0 ? (
                <div className={styles.card}>
                  <h3 className={styles.cardTitle}>
                    Uploaded Photos ({inspection.inspection_images.length})
                  </h3>
                  <div className={styles.photoGrid}>
                    {inspection.inspection_images.map((img) => (
                      <div key={img.id} className={styles.photoItem}>
                        <img src={img.url} alt={img.filename} />
                        <div className={styles.photoName}>{img.filename}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={styles.emptyChecklist}>
                  <span>📭</span>
                  <p>No photos uploaded yet</p>
                </div>
              )}
            </div>
          )}

          {/* ════════ REPORT ════════ */}
          {activeTab === 'report' && (
            <ReportTab
              inspectionId={id}
              existingReport={inspection_report}
              onReportSaved={handleReportSaved}
            />
          )}

        </div>
      </div>
    </InspectorLayout>
  );
}

/* ── Sub-components ── */

function InfoRow({ label, value, mono }) {
  return (
    <div className={styles.infoRow}>
      <span className={styles.infoLabel}>{label}</span>
      <span className={`${styles.infoValue} ${mono ? styles.mono : ''}`}>{value || '—'}</span>
    </div>
  );
}

function InspectionItemRow({ item, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ status: item.status, remarks: item.remarks || '' });
  const statusInfo = ITEM_STATUSES.find((s) => s.value === item.status);

  const handleSave = async () => {
    await onUpdate(item.id, form);
    setEditing(false);
  };

  return (
    <div className={styles.tableRow}>
      <span className={styles.itemName}>{item.name}</span>
      {editing ? (
        <>
          <select
            value={form.status}
            onChange={(e) => setForm({ ...form, status: e.target.value })}
            className={styles.inlineSelect}
          >
            {ITEM_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <input
            className={styles.inlineInput}
            value={form.remarks}
            onChange={(e) => setForm({ ...form, remarks: e.target.value })}
            placeholder="Remarks…"
          />
          <div className={styles.rowActions}>
            <button className={styles.saveSmall}   onClick={handleSave}>Save</button>
            <button className={styles.cancelSmall} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </>
      ) : (
        <>
          <span
            className={styles.statusPill}
            style={{ color: statusInfo?.color, background: `${statusInfo?.color}18` }}
          >
            {statusInfo?.label || item.status}
          </span>
          <span className={styles.remarks}>{item.remarks || '—'}</span>
          <button className={styles.editSmall} onClick={() => setEditing(true)}>Edit</button>
        </>
      )}
    </div>
  );
}